import React, { FC } from 'react';
import { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Grid,
  Alert,
  AlertTitle,
  Typography,
  Box,
  Button,
} from '@mui/material';

import { FormValues } from '../types';

import styles from './results-section.module.scss';

interface ResultsSectionProps {
  errors: FieldErrors<FormValues>;
  gameBuild?: any; // Используем any для совместимости с разными типами GameBuild
  showGameLink: boolean;
  showLimitedVersionMessage?: boolean; // Новый параметр для отображения сообщения об ограниченной версии
}

const ResultsSection: FC<ResultsSectionProps> = ({
  errors,
  gameBuild,
  showGameLink,
  showLimitedVersionMessage = false,
}) => {
  const { t } = useTranslation();
  


  return (
    <>
      {/* Ошибки */}
      {errors.root && (
        <Box sx={{ mt: 2 }} id="form-errors" role="alert">
          <Alert 
            severity="error"
            sx={{
              borderRadius: 3,
              border: '1px solid #f44336',
              backgroundColor: '#ffebee',
              '& .MuiAlert-icon': {
                color: '#d32f2f',
                fontSize: '1.5rem',
              },
              '& .MuiAlert-message': {
                width: '100%',
              },
              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)',
              animation: 'fadeInScale 0.3s ease-out',
              '@keyframes fadeInScale': {
                '0%': {
                  opacity: 0,
                  transform: 'scale(0.95)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'scale(1)',
                },
              },
            }}
          >
            <AlertTitle 
              sx={{ 
                color: '#d32f2f', 
                fontWeight: 600,
                fontSize: '1.1rem',
                mb: 1,
              }}
            >
              {t('clientAll.error')}
            </AlertTitle>
            <Box 
              component="div" 
              sx={{ 
                whiteSpace: 'pre-line',
                fontFamily: 'inherit', 
                margin: 0,
                color: '#c62828',
                fontSize: '0.95rem',
                lineHeight: 1.6,
                '& strong': {
                  color: '#b71c1c',
                  fontWeight: 600,
                },
              }}
            >
              {errors.root.message}
            </Box>
          </Alert>
        </Box>
      )}

      {/* Ссылка на игру */}
      {showGameLink && gameBuild && (
        <Box sx={{ mt: 4 }}>
          <Alert 
            severity="success" 
            className={styles.successAlert}
            sx={{
              '& .MuiAlert-message': {
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              },
              '& .MuiAlert-action': {
                display: 'none'
              },
              '& .MuiAlert-icon': {
                display: 'none' // Скрываем иконку, которая может создавать неравномерные отступы
              },
              padding: '1.5rem', // Устанавливаем одинаковые отступы со всех сторон
              textAlign: 'center' // Центрируем содержимое
            }}
          >
            <AlertTitle className={styles.successAlertTitle}>{t('client.minerGameCreated')}</AlertTitle>
            <Typography variant="body2" gutterBottom>
              <a href={`${window.location.origin}/game?buildId=${gameBuild._id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#065f46', textDecoration: 'underline' }}>
                <strong>{`${window.location.origin}/game?buildId=${gameBuild._id}`}</strong>
              </a>
            </Typography>
            <Box sx={{ 
              mt: 1, 
              display: 'flex', 
              justifyContent: 'center',
              width: '100%'
            }}>
              <Button
                variant="contained"
                size="small"
                className={styles.copyLinkButton}
                onClick={() => navigator.clipboard.writeText(`${window.location.origin}/game?buildId=${gameBuild._id}`)}
                sx={{ minWidth: 'fit-content' }}
              >
                {t('client.copyLink')}
              </Button>
            </Box>
            
            {/* Сообщение об ограниченной версии */}
            {showLimitedVersionMessage && (
              <Box sx={{ 
                mt: 2, 
                textAlign: 'center',
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 1,
                border: '1px solid #e9ecef'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 1, 
                    color: '#495057', 
                    fontWeight: 600 
                  }}
                >
                  {t('clientMazeFree.limitedVersion.resultTitle')}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6c757d',
                    fontSize: '0.875rem',
                    fontStyle: 'italic'
                  }}
                >
                  {t('clientMazeFree.limitedVersion.resultMessage')}
                </Typography>
              </Box>
            )}
          </Alert>
        </Box>
      )}
    </>
  );
};

export default ResultsSection; 